/**
 * Shared GraphQL error message extraction + translation.
 *
 * Extracts the first error message from Apollo Client errors (v4 `errors` or legacy `graphQLErrors`),
 * maps known backend error patterns to i18n keys, and returns a user-friendly translated string.
 *
 * Usage:
 *   catch (err) { setError(getGraphQLErrorMessage(err, t, 'Failed to create folder')); }
 *   catch (err) { toast.error(getGraphQLErrorMessage(err, t, 'Failed to save company profile')); }
 *
 * What the user ends up seeing, in order:
 *   1. a known backend pattern mapped to a translated message (ERROR_PATTERN_MAP);
 *   2. the server's own reason for a 4xx — including the NestJS `ValidationPipe` detail, which is the
 *      only place that names the field it refused (`extensions.exception.response.message`);
 *   3. the caller's `fallbackKey` for everything else — a 5xx, a dead connection, or any plain Error
 *      thrown by our own code. Only a real GraphQL error is ever quoted back to the user.
 *
 * Always pass `fallbackKey`: it is what the user gets whenever the server gave us nothing actionable,
 * so it has to read as a complete sentence on its own.
 */

import { ACCOUNT_LIMITED_MESSAGE } from '@scio3/graphql-client';

/** Safely extract code and message from an unknown error (replaces `err as { code?: string }`). */
export function toErrorLike(err: unknown): { code?: string; message?: string } {
  if (err instanceof Error) {
    const code = 'code' in err && typeof err.code === 'string' ? err.code : undefined;
    return { code, message: err.message };
  }
  if (typeof err === 'object' && err !== null) {
    const obj = err as Record<string, unknown>;
    return {
      code: typeof obj.code === 'string' ? obj.code : undefined,
      message: typeof obj.message === 'string' ? obj.message : undefined,
    };
  }
  return { message: String(err) };
}

/**
 * Known backend error patterns → i18n keys.
 *
 * `network: true` marks a pattern that matches a TRANSPORT-level failure, where Apollo hands us a bare
 * Error with no GraphQL payload. Every other entry is only consulted when the server actually answered,
 * so a message thrown by our own code can never steer the toast (e.g. a REST helper's
 * "…404 Filter not found." used to come out as the far vaguer "The requested resource was not found").
 */
const ERROR_PATTERN_MAP: Array<{ pattern: RegExp; key: string; network?: true }> = [
  // White-label login/signup gate (SCIO 1.0 no_acess_msg) — account isn't branding-entitled or
  // doesn't own/inherit the custom domain. Map the raw code to the friendly message.
  {
    pattern: /NO_BRANDING_ACCESS/,
    key: 'You are not a member of this organization. Please contact your account Administrator.',
  },
  // Enforced account SSO (#2491) — the company requires SSO and this sign-in method isn't allowed.
  {
    pattern: /SSO_REQUIRED/,
    key: "Your organization requires single sign-on. Please sign in with your organization's SSO provider.",
  },
  { pattern: /duplicate key|duplicateFolderName/i, key: 'Duplicate folder name' },
  { pattern: /invalid path/i, key: 'Invalid folder path' },
  // Backend codes are UPPER_SNAKE_CASE (`PERMISSION_DENIED`, principal.service `__pe`) while older
  // messages are prose (`permission denied`) — tolerate both separators (#2138).
  {
    // matches the prose ("permission denied") and the raw backend code ("PERMISSION_DENIED")
    pattern: /permission[ _]denied|forbidden/i,
    key: 'You do not have permission to perform this action',
  },
  // Same shape as PERMISSION_DENIED above: the data-model hooks (team/asset/device/group/
  // playlist/schedule) throw the UPPER_SNAKE code `NOT_FOUND` while older messages are prose
  // (`not found`) — tolerate both separators (#2327).
  { pattern: /not[ _]found/i, key: 'The requested resource was not found' },
  // The shared role/feature guard (open-api `auth/role-feature.guard.ts`) throws these three as bare
  // codes, so they surface on any guarded mutation — add-on buy/cancel, SSO, audit log, …
  { pattern: /\bINSUFFICIENT_ROLE\b/, key: 'You do not have permission to perform this action' },
  { pattern: /\bNOT_AUTHENTICATED\b/, key: 'Please sign in again to continue.' },
  { pattern: /\bFEATURE_NOT_ENABLED\b/, key: 'Not available on your plan' },
  // Decided per code, by what the throw actually carries (open-api `AUTH_EXCEPTION_ERRORS`):
  //  - NOT_AUTHORIZED ships "You don't have access to fulfill the request"; ours is the same thing in
  //    nine languages, so it wins.
  //  - THREAD_ACCESS_DENIED ships "Unable to verify thread ownership" — true but not a sentence a
  //    customer can act on, so ours wins here too.
  //  - AUTH_REQUIRED is thrown single-argument (cdp.service.ts), so nothing else exists, and it means
  //    "sign in", not "not allowed".
  //  - SHARE_LINK_ACCESS_DENIED is deliberately NOT here: "Share link users cannot access this
  //    operation" is more specific than anything generic we would substitute.
  {
    pattern: /\bNOT_AUTHORIZED\b|\bTHREAD_ACCESS_DENIED\b/,
    key: 'You do not have permission to perform this action',
  },
  { pattern: /\bAUTH_REQUIRED\b/, key: 'Please sign in again to continue.' },
  // Same meaning as INSUFFICIENT_ROLE, thrown one-argument by team.resolver.ts for the teams query.
  { pattern: /\bNOT_ADMIN_USER\b/, key: 'You do not have permission to perform this action' },
  {
    pattern: /\bONBOARDING_INVITE_ALREADY_SENT\b/,
    key: 'The onboarding invite has already been sent.',
  },
  // Deliberately NOT mapped, with reasons:
  //  - UNPAIR_ONHOLD: `device.hook.ts` throws it AFTER the unpair has succeeded, as a way to stop the
  //    hook chain. Any message we invent would announce a failure that did not happen, so it keeps the
  //    call site's own copy. The exception-as-control-flow belongs in a backend ticket.
  //  - UNSUPPORTED_TEAM_PURPOSE: a 501 NotImplemented, already withheld by the 5xx rule — a
  //    server-side gap, not something a customer can act on.
  //  - API_NOT_AVAILABLE / API_SCOPE_OFF / API_TOKEN_* / NOT_FOUND_API_KEY: thrown only when the
  //    caller used an API key, which the portal never does; they also carry their own sentence.
  //  - BACKUP_CODE_*, INVITE_LIMIT_EXCEEDED, ROLE_IN_USE_BY_SSO, TEAM_BILLING_ERROR_REMOVE_TEAM:
  //    already translated at their own call sites, which keeps the wording in context.
  // Code-shaped messages the backend uses as i18n keys on purpose; both keys already exist.
  { pattern: /\bduplicateRoleName\b/, key: 'duplicateRoleName' },
  { pattern: /\bduplicateFolderTeamName\b/, key: 'Duplicate folder name shared from other team' },
  { pattern: /not found/i, key: 'The requested resource was not found' },
  // HTTP 413 from the body-parser limit. Apollo's network-level message looks
  // like `Response not successful: Received status code 413`. Map to a clear,
  // actionable hint instead of leaking the raw status code to users.
  {
    pattern: /status code 413|payload too large|request entity too large/i,
    key: 'The upload is too large. Please use a smaller file and try again.',
    network: true,
  },
  // Web Scripting (scriptingAbility) — BE throws raw English from asset.service.ts + asset.utils.ts
  { pattern: /master password is incorrect/i, key: 'Master Password is incorrect.' },
  { pattern: /invalid recorded script/i, key: 'Invalid Recorded Script' },
  // Schedule item duration limits — BE NotAcceptableException from schedule.service.ts
  {
    pattern: /schedule items must be 24h or less/i,
    key: 'To prevent potential conflict, overlaps, schedule items must be 24h or less',
  },
  {
    pattern: /schedule items must be 28 days or less/i,
    key: 'To prevent potential conflict, overlaps, schedule items must be 28 days or less',
  },
];

/** Longest server detail we will put in a toast — a ValidationPipe message can enumerate 34 values. */
const MAX_DETAIL_LENGTH = 220;

/**
 * Status texts and NestJS exception names that tell a user nothing.
 *
 * A `ValidationPipe` rejection arrives with the top-level message "Bad Request Exception" and the real
 * reason ("industry must be one of the following values: …") in `extensions.exception.response.message`.
 * Without this set we show the wrapper and drop the reason (#2500).
 */
const GENERIC_SERVER_MESSAGES = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not acceptable',
  'conflict',
  'unprocessable entity',
  'internal server error',
  'service unavailable',
  'bad gateway',
  'gateway timeout',
  'error',
]);

/** First GraphQL error, whichever array Apollo populated (v4 `errors`, legacy `graphQLErrors`). */
function getFirstGraphQLError(
  error: unknown,
): { message?: string; extensions?: Record<string, unknown> } | undefined {
  const err = error as {
    errors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
    graphQLErrors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
  };
  return err?.errors?.[0] ?? err?.graphQLErrors?.[0];
}

/**
 * Only PROSE reaches a customer — never a machine code.
 *
 * The product rule is that a banner shows a reason, not an identifier, so `INSUFFICIENT_ROLE`,
 * `FEATURE_NOT_ENABLED:optidev`, `duplicateRoleName` and `RECAPTCHA_CHALLENGE_REQUIRED` must not be
 * rendered even though they arrive as the server's message. A code that HAS a reason is translated by
 * ERROR_PATTERN_MAP above; anything left over falls back to the caller's own sentence.
 *
 * The test is simply "does it read like a sentence": a reason has a space in it, an identifier does
 * not. That also covers camelCase and single-token codes, which an UPPER_SNAKE pattern would miss, and
 * it cannot swallow a real message — the shortest one the backend sends ("Your card was declined.")
 * has spaces.
 */
function looksLikeProse(message: string): boolean {
  return /\S\s+\S/.test(message.trim());
}

function isMeaningfulMessage(message: string | undefined): message is string {
  if (!message) return false;
  const trimmed = message.trim();
  if (!looksLikeProse(trimmed)) return false;
  const normalized = trimmed
    .replace(/\.$/, '')
    .replace(/ exception$/i, '')
    .toLowerCase();
  return normalized.length > 0 && !GENERIC_SERVER_MESSAGES.has(normalized);
}

function truncateDetail(detail: string): string {
  if (detail.length <= MAX_DETAIL_LENGTH) return detail;
  const cut = detail.slice(0, MAX_DETAIL_LENGTH);
  const breakAt = Math.max(cut.lastIndexOf(' '), cut.lastIndexOf(','));
  return `${cut.slice(0, breakAt > MAX_DETAIL_LENGTH / 2 ? breakAt : MAX_DETAIL_LENGTH).trimEnd()}…`;
}

/**
 * Extract the NestJS exception body's own message — the only place a `ValidationPipe` rejection names
 * the field it refused. open-api re-attaches that body in `formatError` (`app.module.ts`), so it lives
 * at `extensions.exception.response.message`: a string for a thrown HttpException, an array of
 * per-field strings for a validation failure.
 */
export function getServerErrorDetail(error: unknown): string | undefined {
  const response = (
    getFirstGraphQLError(error)?.extensions?.exception as Record<string, unknown> | undefined
  )?.response as Record<string, unknown> | undefined;
  const message = response?.message;

  if (Array.isArray(message)) {
    const reasons = message.filter(
      (part): part is string => typeof part === 'string' && part.trim().length > 0,
    );
    return reasons.length > 0 ? reasons.join('; ') : undefined;
  }
  return typeof message === 'string' && message.trim().length > 0 ? message : undefined;
}

/**
 * The server's literal message, with NO audience gate — for CLASSIFYING an error, never for showing it.
 *
 * `getGraphQLErrorMessage` deliberately withholds text it cannot vouch for (anything without a 4xx
 * status). A caller that needs to MATCH on what the backend actually said — e.g. recognising the
 * `SOCIAL_LOGIN_ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL` constant, which arrives as the exception
 * message with Apollo's generic `INTERNAL_SERVER_ERROR` code and no status — needs the raw string.
 *
 * Do NOT render the result to a customer. If you want something to show, use
 * `getGraphQLErrorMessage(err, t, fallback)`.
 */
export function getRawServerMessage(error: unknown): string {
  const err = error as {
    graphQLErrors?: Array<{ message?: string }>;
    errors?: Array<{ message?: string }>;
    message?: string;
  };
  return err?.graphQLErrors?.[0]?.message ?? err?.errors?.[0]?.message ?? err?.message ?? '';
}

/**
 * The human sentence a two-argument NestJS throw carries.
 *
 * `new UnauthorizedException('API_NOT_AVAILABLE', AUTH_EXCEPTION_ERRORS.API_NOT_AVAILABLE)` puts the
 * CODE in `response.message` and the SENTENCE in `response.error` (`@nestjs/common`
 * `HttpException.createBody`), and open-api's `AUTH_EXCEPTION_ERRORS` is a map of exactly that:
 * code → user-facing copy ("The request is not available for API key", "Your account needs
 * attention. Please log on to management portal and update."). Reading only `response.message` left
 * every one of those failures showing the caller's generic fallback while the server's own reason sat
 * unread one field away.
 *
 * `response.error` is overloaded, though: the single-argument form puts the HTTP status text there
 * ("Unauthorized"), and the two-arg form is also used code-in-both-slots
 * (`ForbiddenException('PERMISSION_DENIED', 'DEVICE_FOLDER_NOT_EMPTY')`, which `getMoveErrorReason`
 * reads). The caller filters both out: a status text via GENERIC_SERVER_MESSAGES, a code because it
 * is not prose.
 */
export function getServerErrorDescription(error: unknown): string | undefined {
  const response = (
    getFirstGraphQLError(error)?.extensions?.exception as Record<string, unknown> | undefined
  )?.response as Record<string, unknown> | undefined;
  const description = response?.error;
  return typeof description === 'string' && description.trim().length > 0 ? description : undefined;
}

/**
 * The sentence an OBJECT-bodied NestJS throw carries on `.detail`.
 *
 * `throw new BadRequestException({ message: 'MEMBER_IS_GROUP_DERIVED', detail: '...' })` puts the
 * whole object at `response`, so `response.message` holds the CODE and the sentence sits on
 * `.detail` — neither `getServerErrorDetail` (reads `.message`) nor `getServerErrorDescription`
 * (reads `.error`) can see it. That one unread field was the whole of scio3#2425: the code reached
 * the end of the chain, nothing was meaningful, and the toast showed "Something went wrong. Please
 * try again." while the backend had composed "X is in this teamspace through the group Y, which sets
 * their role."
 *
 * Read here rather than added to `ERROR_PATTERN_MAP` on purpose. The map is checked FIRST and WINS
 * (see `getGraphQLErrorMessage`), so a pattern entry would SUPPRESS this sentence in favour of
 * generic copy — losing the group name, which is the only part an admin can act on. The map's
 * "translated copy wins" rule is right for codes whose message is a fixed string; it is wrong for one
 * the server composes from live data, and no translation can name the group.
 */
function getServerErrorObjectDetail(error: unknown): string | undefined {
  const response = (
    getFirstGraphQLError(error)?.extensions?.exception as Record<string, unknown> | undefined
  )?.response as Record<string, unknown> | undefined;
  const detail = response?.detail;
  return typeof detail === 'string' && detail.trim().length > 0 ? detail : undefined;
}

/**
 * A bare single-token message — the shape of an i18n KEY the backend threw on purpose.
 *
 * Some exceptions carry a translation key rather than prose: `role.service.ts` throws
 * `BadRequestException('duplicateRoleName')` for a name collision, and `en.json` has
 * `"duplicateRoleName": "Role name already exists"` waiting for it (#2377).
 *
 * Deliberately wide, and checked ONLY against the translation catalogue: a token with no translation
 * falls through untouched, so this can only ever replace an identifier with real copy — never mangle
 * a genuine one-word server message.
 */
const BARE_KEY_RE = /^[A-Za-z][A-Za-z0-9_.]*$/;

/**
 * An UPPER_SNAKE internal code (`NOT_FOUND`, `BACKUP_CODE_USED`). Excluded from the catalogue lookup
 * above even when `en.json` happens to hold that key.
 *
 * A code can have copy in the catalogue because ONE screen handles it in context — `BACKUP_CODE_USED`
 * is MFA's, shown where a backup code was just spent. Translating it wherever it surfaces would put
 * "You used a backup code" on a company-profile toast. Codes earn their copy per call site, through
 * `ERROR_PATTERN_MAP`; the catalogue lookup is only for the camelCase KEYS the backend throws as keys
 * on purpose. Pinned by the toast matrix (`graphql-error-matrix.test.ts`).
 */
const UPPER_SNAKE_CODE_RE = /^[A-Z][A-Z0-9_]*$/;

/**
 * Extract user-friendly error message from an Apollo GraphQL error.
 *
 * @param error - The caught error (Apollo ApolloError, CombinedGraphQLErrors, or plain Error)
 * @param t - i18next `t()` function for translation. If provided, known patterns are translated.
 * @param fallbackKey - i18n key to use when no message can be extracted
 * @returns Translated or raw error message string
 */
export function getGraphQLErrorMessage(
  error: unknown,
  t?: (key: string) => string,
  fallbackKey?: string,
): string {
  const rawMsg = getRawServerMessage(error);
  const detail = getServerErrorDetail(error);

  const hasServerAnswer = getFirstGraphQLError(error) !== undefined;

  // Translated copy wins where we have it: the pattern map is localizable, the server's sentence is
  // English only.
  if (t) {
    for (const { pattern, key, network } of ERROR_PATTERN_MAP) {
      if (!hasServerAnswer && !network) continue;
      if (pattern.test(detail ?? '') || pattern.test(rawMsg)) return t(key);
    }
  }

  // #2377 — the backend throws SOME errors as i18n KEYS on purpose (`duplicateRoleName`). They are
  // camelCase, so the pattern map misses any that nobody has listed and `looksLikeProse` rejects them
  // as non-prose, which left the raw token reaching the toast.
  //
  // Resolved against the CATALOGUE, never rendered raw — an unknown token falls through untouched.
  // That is also why this sits ahead of the `hasServerAnswer` / 4xx gates below: what it returns is
  // OUR translated copy, not text we are quoting back from the server, so those gates have nothing to
  // protect against here. It matters because a mutation rejection reaches `RolesWorkbench` as a plain
  // `Error` as readily as an ApolloError, and the copy must not depend on which one Apollo hands over.
  if (t) {
    const token = (getServerErrorDetail(error) ?? rawMsg).trim();
    if (token && BARE_KEY_RE.test(token) && !UPPER_SNAKE_CODE_RE.test(token)) {
      const translated = t(token);
      if (translated !== token) return translated;
    }
  }

  const fallbackMessage = fallbackKey ?? 'An error occurred';
  const fallback = t?.(fallbackMessage) ?? fallbackMessage;

  // Only ever quote the SERVER back to the user. A plain Error reaching here is a dead connection or
  // a bug in our own code ("Cannot read properties of undefined"), and its text helps nobody.
  if (!hasServerAnswer) return fallback;

  // Quote the server only when it answered with a CLIENT-error status.
  //
  // `hasServerAnswer` is not enough on its own: Apollo wraps a resolver-thrown `Error`, a Mongo /
  // Mongoose driver error and its own variable-coercion failures in a GraphQL error object too, and
  // open-api's `formatError` does not redact their text in non-debug mode. Those carry no
  // `statusCode`, so without this gate they were quoted verbatim — "Cast to ObjectId failed … for
  // model \"Device\"", "Device validation failed: accountId: Path `accountId` is required."
  //
  // Requiring a resolvable 4xx keeps exactly what this helper exists for (a NestJS HttpException,
  // which always carries one) and drops everything that only looks like a server answer.
  const status = getGraphQLErrorStatus(error);
  if (status === undefined || status < 400 || status >= 500) return fallback;

  // The validation detail names the field; the top-level message is usually just the status text.
  if (isMeaningfulMessage(detail)) return truncateDetail(detail);
  // Then the second throw argument, where a coded throw keeps its sentence.
  const description = getServerErrorDescription(error);
  if (isMeaningfulMessage(description)) return truncateDetail(description);
  // Then an OBJECT-bodied throw's `.detail`, the only slot a server-composed sentence lands in.
  const objectDetail = getServerErrorObjectDetail(error);
  if (isMeaningfulMessage(objectDetail)) return truncateDetail(objectDetail);
  if (isMeaningfulMessage(rawMsg)) return rawMsg;
  return fallback;
}

/**
 * Extract the custom error code from a GraphQL error.
 *
 * NestJS ForbiddenException({ message, code }) serializes as:
 *   - extensions.exception.response.code (debug/dev mode)
 *   - extensions.code (production GraphQL error code, e.g. 'FORBIDDEN')
 *   - message (fallback: the error message itself)
 *
 * Checks nested NestJS exception response first, then recognizes the CAPTCHA
 * challenge message wrapped by FORBIDDEN, then checks extensions.code and message.
 */
export function getGraphQLErrorCode(error: unknown): string | undefined {
  const err = error as {
    graphQLErrors?: Array<{
      message?: string;
      extensions?: Record<string, unknown>;
    }>;
    errors?: Array<{
      message?: string;
      extensions?: Record<string, unknown>;
    }>;
    message?: string;
  };

  const firstError = err?.errors?.[0] ?? err?.graphQLErrors?.[0];
  if (!firstError) return undefined;

  // NestJS nested exception response (debug mode)
  const ext = firstError.extensions;
  const nestedResponse = (ext?.exception as Record<string, unknown> | undefined)?.response as
    | Record<string, unknown>
    | undefined;
  if (typeof nestedResponse?.code === 'string') return nestedResponse.code;

  // NestJS may wrap the CAPTCHA challenge in a generic FORBIDDEN code without
  // a nested application code. Preserve the challenge so callers can show v2.
  if (ext?.code === 'FORBIDDEN' && firstError.message === 'RECAPTCHA_CHALLENGE_REQUIRED') {
    return firstError.message;
  }

  // Top-level GraphQL error code
  if (typeof ext?.code === 'string') return ext.code;

  // Message-based fallback
  return firstError.message ?? undefined;
}

/**
 * Extract specific error reason from GraphQL error extensions.
 * Backend throws ForbiddenException("PERMISSION_DENIED", "SPECIFIC_CODE")
 * → extensions.exception.response.error = "SPECIFIC_CODE"
 * The returned code can be used directly as an i18n translation key.
 */
export function getMoveErrorReason(err: unknown): string | null {
  const gqlError = err as {
    // Apollo Client 4 uses `errors`
    errors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
    // Legacy Apollo Client uses `graphQLErrors`
    graphQLErrors?: Array<{ message?: string; extensions?: Record<string, unknown> }>;
  };
  const firstError = gqlError?.errors?.[0] ?? gqlError?.graphQLErrors?.[0];
  if (!firstError) return null;

  // Two-arg pattern: specific reason in extensions.exception.response.error
  const ext = firstError.extensions;
  const response = (ext?.exception as Record<string, unknown> | undefined)?.response as
    | Record<string, unknown>
    | undefined;
  const reason = response?.error;
  // Filter out generic HTTP status texts (e.g. "Not Acceptable", "Forbidden") —
  // only return app-specific error codes like "DEVICE_FOLDER_NOT_EMPTY".
  // HTTP status texts are title-cased words without underscores; app codes use UPPER_SNAKE_CASE.
  if (typeof reason === 'string' && /^[A-Z][A-Z0-9_]*(_[A-Z0-9]+)+$/.test(reason)) return reason;

  // Single-arg pattern: error code is the message itself (e.g. "duplicateFolderName").
  // Map known camelCase codes to existing i18n keys.
  const MESSAGE_CODE_MAP: Record<string, string> = {
    duplicateFolderName: 'Duplicate folder name',
    duplicateFolderTeamName: 'Duplicate folder name shared from other team',
  };
  if (typeof firstError.message === 'string' && firstError.message in MESSAGE_CODE_MAP) {
    return MESSAGE_CODE_MAP[firstError.message];
  }

  return null;
}

/**
 * Extract the HTTP status code from a GraphQL error.
 *
 * NestJS HttpExceptions surface their status in several places depending on mode:
 *   - extensions.status (Apollo formatted error)
 *   - extensions.exception.status / extensions.exception.response.statusCode (debug mode)
 *   - extensions.originalError.statusCode
 *
 * Use this to distinguish error classes reliably instead of matching message text —
 * e.g. a genuine overlap is a ConflictException (409) while a duration-limit rejection is a
 * NotAcceptableException (406), and both messages contain the words "conflict"/"overlaps".
 */
export function getGraphQLErrorStatus(error: unknown): number | undefined {
  const err = error as {
    errors?: Array<{ extensions?: Record<string, unknown> }>;
    graphQLErrors?: Array<{ extensions?: Record<string, unknown> }>;
  };
  const ext = (err?.errors?.[0] ?? err?.graphQLErrors?.[0])?.extensions;
  if (!ext) return undefined;

  const exception = ext.exception as Record<string, unknown> | undefined;
  const exceptionResponse = exception?.response as Record<string, unknown> | undefined;
  const originalError = ext.originalError as Record<string, unknown> | undefined;

  const candidates = [
    ext.status,
    exception?.status,
    exceptionResponse?.statusCode,
    originalError?.statusCode,
  ];
  const status = candidates.find((v) => typeof v === 'number');
  return typeof status === 'number' ? status : undefined;
}

/**
 * True when the error is the dunning enforcement guard refusing a write (open-api
 * `DunningEnforcementGuard` throws `ACCOUNT_LIMITED`, tagged UNAUTHENTICATED).
 *
 * The guard's identity lives in the MESSAGE, not `extensions.code` (that is just
 * UNAUTHENTICATED), and the GraphQL client's error link rewrites the per-error
 * message in place to `ACCOUNT_LIMITED_MESSAGE`. So this matches the code OR the
 * exported constant (never a hand-copied sentence), on the per-error entry and on
 * the combined error's own message (built from the raw code before the rewrite).
 *
 * Callers use it to show their own translated, action-specific line instead of
 * rendering the untranslated English sentence inline.
 */
export function isAccountLimitedError(error: unknown): boolean {
  const err = error as {
    errors?: Array<{ message?: string }>;
    graphQLErrors?: Array<{ message?: string }>;
    message?: string;
  };
  const matches = (msg: unknown) =>
    typeof msg === 'string' &&
    (msg.includes('ACCOUNT_LIMITED') || msg.includes(ACCOUNT_LIMITED_MESSAGE));
  if (!err || typeof err !== 'object') return false;
  const entries = [...(err.errors ?? []), ...(err.graphQLErrors ?? [])];
  return entries.some((e) => matches(e?.message)) || matches(err.message);
}
